import { api } from "../api";
import { Icons } from "../utils/icons";
import type { SharedDocument, SchoolClass, Practice, SharedDistributeTarget } from "../types";
import { esc } from "../utils/html";

const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png"];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

// Modul-State der aktuell offenen Seite
let activeClasses: SchoolClass[] = [];
let linkedPractices: Practice[] = [];
let classById = new Map<number, SchoolClass>();
let selectedFile: File | null = null;
let targetKind: "student" | "practice" = "student";
let lastDocs: SharedDocument[] = [];

export async function render(el: HTMLElement) {
  selectedFile = null;
  targetKind = "student";
  el.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:200px"><div class="spinner"></div></div>`;

  try {
    const [classes, practices] = await Promise.all([
      api.classes.list(),
      api.practices.list(),
    ]);
    activeClasses = classes.filter((c) => c.active);
    classById = new Map(activeClasses.map((c) => [c.id, c]));
    linkedPractices = practices.filter((p) => p.practiceOrganizationId != null);
  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Fehler beim Laden: ${esc(err.message)}</div>`;
    return;
  }

  el.innerHTML = renderLayout();
  bind(el);
  void refreshList(el.querySelector<HTMLElement>("#sd-list")!);
}

function renderLayout(): string {
  return `
    <div>
      <div class="settings-page-header">
        <div>
          <h1>Dokumente verteilen</h1>
          <p>
            Eine Datei einmal hochladen und an mehrere Schüler oder Praktikumsstellen verteilen.
            Beim Entfernen bei einem einzelnen Empfänger bleibt die zentrale Datei erhalten.
          </p>
        </div>
      </div>

      <div class="card" style="padding:1.25rem;margin-bottom:1.75rem">
        <div id="sd-dropzone"
          style="border:2px dashed var(--primary-light);border-radius:.5rem;padding:1.5rem 1rem;background:var(--primary-50);text-align:center;cursor:pointer;transition:border-color .15s,background .15s">
          <div style="display:inline-block;width:32px;height:32px;color:var(--primary);margin-bottom:.4rem">${Icons.arrowUpTray}</div>
          <div id="sd-dropzone-label" style="font-size:.9rem;color:var(--gray-700)">
            <span style="color:var(--primary);font-weight:600">Datei auswählen</span>
            oder hierher ziehen
          </div>
          <div style="font-size:.75rem;color:var(--gray-500);margin-top:.3rem">PDF, JPG, PNG &middot; max. 10&nbsp;MB</div>
          <input type="file" id="sd-file" hidden
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" />
        </div>

        <div style="margin-top:1.25rem">
          <div style="font-weight:600;font-size:.9rem;color:var(--gray-800);margin-bottom:.5rem">Empfänger</div>
          <div style="display:inline-flex;border:1px solid var(--gray-300);border-radius:.375rem;overflow:hidden">
            <button type="button" id="sd-tab-student" class="sd-tab"
              style="padding:.45rem .9rem;border:none;background:var(--primary);color:#fff;font-size:.875rem;cursor:pointer">Schüler</button>
            <button type="button" id="sd-tab-practice" class="sd-tab"
              style="padding:.45rem .9rem;border:none;background:#fff;color:var(--gray-700);font-size:.875rem;cursor:pointer">Praktikumsstellen</button>
          </div>
        </div>

        <div id="sd-panel-student" style="margin-top:1rem">
          <label style="display:flex;align-items:center;gap:.5rem;font-size:.9rem;color:var(--gray-800);margin-bottom:.4rem;cursor:pointer">
            <input type="radio" name="sd-scope" value="all" checked style="accent-color:var(--primary)" />
            Alle aktiven Schüler
          </label>
          <label style="display:flex;align-items:center;gap:.5rem;font-size:.9rem;color:var(--gray-800);cursor:pointer">
            <input type="radio" name="sd-scope" value="classes" style="accent-color:var(--primary)" />
            Bestimmte Klassen
          </label>
          <div id="sd-classes" style="margin-top:.6rem;padding-left:1.6rem;display:none;flex-direction:column;gap:.35rem">
            ${
              activeClasses.length === 0
                ? `<span style="color:var(--gray-500);font-size:.85rem">Keine aktiven Klassen vorhanden.</span>`
                : activeClasses
                    .map(
                      (c) => `
              <label style="display:flex;align-items:center;gap:.5rem;font-size:.875rem;color:var(--gray-700);cursor:pointer">
                <input type="checkbox" class="sd-class" value="${c.id}" style="accent-color:var(--primary)" />
                ${esc(c.name)} <span style="color:var(--gray-400)">(${c.studentCount})</span>
              </label>`
                    )
                    .join("")
            }
          </div>
        </div>

        <div id="sd-panel-practice" hidden style="margin-top:1rem">
          ${
            linkedPractices.length === 0
              ? `<div style="font-size:.85rem;color:var(--gray-600);background:var(--gray-50);border:1px solid var(--gray-200);border-radius:.375rem;padding:.6rem .75rem">Es sind keine verknüpften Praktikumsstellen vorhanden. Nur verknüpfte Stellen mit Portal-Zugang können Dokumente empfangen.</div>`
              : `<div style="font-size:.8rem;color:var(--gray-500);margin-bottom:.5rem">Nur verknüpfte Stellen mit Portal-Zugang werden angezeigt.</div>
                 <div id="sd-practices" style="display:flex;flex-direction:column;gap:.35rem;max-height:240px;overflow-y:auto">
                   ${linkedPractices
                     .map(
                       (p) => `
                     <label style="display:flex;align-items:center;gap:.5rem;font-size:.875rem;color:var(--gray-700);cursor:pointer">
                       <input type="checkbox" class="sd-practice" value="${p.id}" style="accent-color:var(--primary)" />
                       ${esc(p.name)}${p.ort ? ` <span style="color:var(--gray-400)">(${esc(p.ort)})</span>` : ""}
                     </label>`
                     )
                     .join("")}
                 </div>`
          }
        </div>

        <div style="margin-top:1.25rem;display:flex;align-items:center;gap:.75rem">
          <button type="button" id="sd-distribute" class="btn btn-primary" disabled>Verteilen</button>
          <span id="sd-distribute-hint" style="font-size:.8rem;color:var(--gray-500)"></span>
        </div>
      </div>

      <h2 style="font-size:1.1rem;font-weight:600;margin:0 0 .75rem">Bereits verteilt</h2>
      <div id="sd-list"><p style="color:var(--gray-500);font-size:.9rem">Lade…</p></div>
    </div>`;
}

function bind(root: HTMLElement) {
  const dropzone = root.querySelector<HTMLElement>("#sd-dropzone")!;
  const dropLabel = root.querySelector<HTMLElement>("#sd-dropzone-label")!;
  const fileInput = root.querySelector<HTMLInputElement>("#sd-file")!;
  const tabStudent = root.querySelector<HTMLButtonElement>("#sd-tab-student")!;
  const tabPractice = root.querySelector<HTMLButtonElement>("#sd-tab-practice")!;
  const panelStudent = root.querySelector<HTMLElement>("#sd-panel-student")!;
  const panelPractice = root.querySelector<HTMLElement>("#sd-panel-practice")!;
  const classesBox = root.querySelector<HTMLElement>("#sd-classes")!;
  const distributeBtn = root.querySelector<HTMLButtonElement>("#sd-distribute")!;

  const setFile = (file: File | null) => {
    if (file) {
      const nameLower = file.name.toLowerCase();
      if (!ALLOWED_EXT.some((ext) => nameLower.endsWith(ext))) {
        alert("Nur PDF, JPG oder PNG erlaubt.");
        fileInput.value = "";
        return;
      }
      if (file.size > MAX_SIZE_BYTES) {
        alert("Datei überschreitet 10 MB.");
        fileInput.value = "";
        return;
      }
    }
    selectedFile = file;
    dropLabel.innerHTML = file
      ? `<span style="color:var(--primary);font-weight:600">${esc(file.name)}</span> ausgewählt`
      : `<span style="color:var(--primary);font-weight:600">Datei auswählen</span> oder hierher ziehen`;
    updateState(root);
  };

  dropzone.addEventListener("click", () => fileInput.click());
  fileInput.addEventListener("change", () => setFile(fileInput.files?.[0] || null));
  dropzone.addEventListener("dragover", (e) => {
    e.preventDefault();
    dropzone.style.borderColor = "var(--primary)";
    dropzone.style.background = "var(--primary-light)";
  });
  dropzone.addEventListener("dragleave", () => {
    dropzone.style.borderColor = "var(--primary-light)";
    dropzone.style.background = "var(--primary-50)";
  });
  dropzone.addEventListener("drop", (e) => {
    e.preventDefault();
    dropzone.style.borderColor = "var(--primary-light)";
    dropzone.style.background = "var(--primary-50)";
    setFile(e.dataTransfer?.files?.[0] || null);
  });

  const selectTab = (kind: "student" | "practice") => {
    targetKind = kind;
    const active = "background:var(--primary);color:#fff";
    const inactive = "background:#fff;color:var(--gray-700)";
    tabStudent.style.cssText = `padding:.45rem .9rem;border:none;font-size:.875rem;cursor:pointer;${kind === "student" ? active : inactive}`;
    tabPractice.style.cssText = `padding:.45rem .9rem;border:none;font-size:.875rem;cursor:pointer;${kind === "practice" ? active : inactive}`;
    panelStudent.hidden = kind !== "student";
    panelPractice.hidden = kind !== "practice";
    updateState(root);
  };
  tabStudent.addEventListener("click", () => selectTab("student"));
  tabPractice.addEventListener("click", () => selectTab("practice"));

  root.querySelectorAll<HTMLInputElement>("input[name='sd-scope']").forEach((radio) => {
    radio.addEventListener("change", () => {
      const scope = root.querySelector<HTMLInputElement>("input[name='sd-scope']:checked")?.value || "all";
      classesBox.style.display = scope === "classes" ? "flex" : "none";
      updateState(root);
    });
  });

  root.querySelectorAll<HTMLInputElement>(".sd-class, .sd-practice").forEach((cb) => {
    cb.addEventListener("change", () => updateState(root));
  });

  distributeBtn.addEventListener("click", () => void distribute(root));
}

/** Aktiviert/deaktiviert den Verteilen-Button je nach Auswahl. */
function updateState(root: HTMLElement) {
  const distributeBtn = root.querySelector<HTMLButtonElement>("#sd-distribute")!;
  distributeBtn.disabled = !canDistribute(root);
}

function canDistribute(root: HTMLElement): boolean {
  if (!selectedFile) return false;
  if (targetKind === "student") {
    const scope = root.querySelector<HTMLInputElement>("input[name='sd-scope']:checked")?.value || "all";
    if (scope === "all") return true;
    return selectedClassIds(root).length > 0;
  }
  return selectedPracticeIds(root).length > 0;
}

function selectedClassIds(root: HTMLElement): number[] {
  return [...root.querySelectorAll<HTMLInputElement>(".sd-class:checked")].map((cb) => Number(cb.value));
}

function selectedPracticeIds(root: HTMLElement): number[] {
  return [...root.querySelectorAll<HTMLInputElement>(".sd-practice:checked")].map((cb) => Number(cb.value));
}

async function distribute(root: HTMLElement) {
  if (!selectedFile || !canDistribute(root)) return;
  const distributeBtn = root.querySelector<HTMLButtonElement>("#sd-distribute")!;

  let target: SharedDistributeTarget;
  if (targetKind === "student") {
    const scope = root.querySelector<HTMLInputElement>("input[name='sd-scope']:checked")?.value || "all";
    target =
      scope === "classes"
        ? { targetKind: "student", scope: "classes", classIds: selectedClassIds(root) }
        : { targetKind: "student", scope: "all" };
  } else {
    target = { targetKind: "practice", practiceIds: selectedPracticeIds(root) };
  }

  distributeBtn.disabled = true;
  const orig = distributeBtn.textContent;
  distributeBtn.textContent = "Verteile…";
  try {
    const res = await api.sharedDocuments.distribute(selectedFile, target);
    const n = res.recipientCount;
    showToast(n > 0 ? `Verteilt an ${n} Empfänger.` : "Keine passenden Empfänger gefunden.");
    resetForm(root);
    await refreshList(root.querySelector<HTMLElement>("#sd-list")!);
  } catch (err: any) {
    alert("Verteilen fehlgeschlagen: " + (err?.error || err?.message || "unbekannter Fehler"));
  } finally {
    distributeBtn.textContent = orig;
    updateState(root);
  }
}

function resetForm(root: HTMLElement) {
  selectedFile = null;
  const fileInput = root.querySelector<HTMLInputElement>("#sd-file")!;
  const dropLabel = root.querySelector<HTMLElement>("#sd-dropzone-label")!;
  fileInput.value = "";
  dropLabel.innerHTML = `<span style="color:var(--primary);font-weight:600">Datei auswählen</span> oder hierher ziehen`;
  root.querySelectorAll<HTMLInputElement>(".sd-class:checked, .sd-practice:checked").forEach((cb) => (cb.checked = false));
}

async function refreshList(listEl: HTMLElement) {
  try {
    const docs = await api.sharedDocuments.list();
    renderList(listEl, docs);
  } catch (err: any) {
    listEl.innerHTML = `<p style="color:var(--red-700);font-size:.9rem">Fehler beim Laden: ${esc(err.message)}</p>`;
  }
}

function renderList(listEl: HTMLElement, docs: SharedDocument[]) {
  lastDocs = docs;
  if (docs.length === 0) {
    listEl.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;gap:.5rem;padding:2rem 1rem;color:var(--gray-500);text-align:center">
        <span style="display:inline-flex;align-items:center;justify-content:center;width:48px;height:48px;border-radius:.5rem;background:var(--gray-100);color:var(--gray-400)">
          <span style="display:inline-block;width:26px;height:26px">${Icons.documentText}</span>
        </span>
        <p style="font-size:.9rem;margin:0">Noch nichts verteilt.</p>
      </div>`;
    return;
  }

  const rows = docs
    .map(
      (d) => `
    <tr>
      <td style="word-break:break-word">
        <span style="font-weight:500;color:var(--gray-900)" title="${esc(d.originalName)}">${esc(d.originalName)}</span>
      </td>
      <td style="color:var(--gray-700);word-break:break-word">${esc(targetLabel(d))}</td>
      <td style="white-space:nowrap;color:var(--gray-700);text-align:center">${d.recipientCount}</td>
      <td style="white-space:nowrap;color:var(--gray-700)">${formatDate(d.createdAt)}</td>
      <td>
        <div style="display:flex;flex-wrap:wrap;gap:.35rem;justify-content:flex-end">
          <button class="btn btn-secondary btn-sm sd-download" data-id="${d.id}" data-name="${esc(d.originalName)}" title="Herunterladen" aria-label="Herunterladen">
            <span style="display:inline-block;width:16px;height:16px;vertical-align:-3px">${Icons.arrowDownTray}</span>
          </button>
          <button class="btn btn-secondary btn-sm sd-sync" data-id="${d.id}">Neue Empfänger ergänzen</button>
          <button class="btn btn-danger btn-sm sd-withdraw" data-id="${d.id}" data-name="${esc(d.originalName)}">Zurückziehen</button>
        </div>
      </td>
    </tr>`
    )
    .join("");

  listEl.innerHTML = `
    <table style="width:100%;table-layout:auto">
      <thead>
        <tr>
          <th>Name</th>
          <th>Zielgruppe</th>
          <th style="text-align:center">Empfänger</th>
          <th>Datum</th>
          <th style="text-align:right">Aktionen</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>`;

  bindListHandlers(listEl);
}

function bindListHandlers(listEl: HTMLElement) {
  listEl.querySelectorAll<HTMLButtonElement>(".sd-download").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const id = Number(btn.dataset.id);
      try {
        await api.sharedDocuments.download(id, btn.dataset.name || `dokument-${id}`);
      } catch (err: any) {
        alert("Download fehlgeschlagen: " + (err?.message || "unbekannter Fehler"));
      }
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>(".sd-sync").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = Number(btn.dataset.id);
      const doc = lastDocs.find((d) => d.id === id);
      if (doc) void openAddRecipientsDialog(doc, listEl);
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>(".sd-withdraw").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const id = Number(btn.dataset.id);
      const name = btn.dataset.name || "dieses Dokument";
      if (
        !confirm(
          `„${name}" für alle Empfänger zurückziehen?\n\nDie Datei wird bei allen Empfängern entfernt und unwiderruflich gelöscht.`
        )
      )
        return;
      btn.disabled = true;
      try {
        await api.sharedDocuments.withdraw(id);
        showToast("Verteilung zurückgezogen.");
        await refreshList(listEl);
      } catch (err: any) {
        alert("Zurückziehen fehlgeschlagen: " + (err?.message || "unbekannter Fehler"));
        btn.disabled = false;
      }
    });
  });
}

/** Dialog: Empfänger (Schüler bzw. verknüpfte Stellen) zu einer Verteilung ergänzen. */
async function openAddRecipientsDialog(doc: SharedDocument, listEl: HTMLElement) {
  let data: {
    targetKind: "student" | "practice";
    candidates: { id: number; label: string; sub: string | null }[];
  };
  try {
    data = await api.sharedDocuments.candidates(doc.id);
  } catch (err: any) {
    alert("Mögliche Empfänger konnten nicht geladen werden: " + (err?.message || "unbekannter Fehler"));
    return;
  }

  const isStudent = data.targetKind === "student";
  const candidates = data.candidates;
  const empty = candidates.length === 0;
  const title = isStudent ? "Schüler ergänzen" : "Praktikumsstellen ergänzen";
  const intro = empty
    ? (isStudent
        ? "Alle aktiven Schüler haben dieses Dokument bereits."
        : "Alle verknüpften Praktikumsstellen haben dieses Dokument bereits.")
    : (isStudent
        ? "Wähle die Schüler aus, die das Dokument zusätzlich erhalten sollen."
        : "Wähle die verknüpften Stellen aus, die das Dokument zusätzlich erhalten sollen.");

  const overlay = document.createElement("div");
  overlay.style.cssText =
    "position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:10010;display:flex;align-items:center;justify-content:center";
  overlay.innerHTML = `
    <div role="dialog" aria-modal="true"
      style="background:#fff;border-radius:.5rem;box-shadow:0 10px 25px rgba(0,0,0,.2);width:min(32rem,calc(100% - 2rem));max-height:90vh;display:flex;flex-direction:column">
      <div style="display:flex;align-items:center;justify-content:space-between;padding:.875rem 1.25rem;border-bottom:1px solid var(--gray-100)">
        <h2 style="font-size:1.05rem;font-weight:600;margin:0">${title}</h2>
        <button type="button" id="ar-close" aria-label="Schließen"
          style="background:none;border:none;font-size:1.25rem;line-height:1;cursor:pointer;color:var(--gray-500)">×</button>
      </div>
      <div style="padding:1rem 1.25rem;overflow-y:auto">
        <p style="font-size:.85rem;color:var(--gray-600);margin:0 0 ${empty ? "0" : ".75rem"}">${intro}</p>
        ${
          empty
            ? ""
            : `<label style="display:flex;align-items:center;gap:.5rem;font-size:.8rem;color:var(--gray-700);font-weight:600;margin-bottom:.5rem;cursor:pointer">
                 <input type="checkbox" id="ar-all" style="accent-color:var(--primary)" /> Alle auswählen
               </label>
               <div style="display:flex;flex-direction:column;gap:.35rem;max-height:300px;overflow-y:auto">
                 ${candidates
                   .map(
                     (c) => `
                   <label style="display:flex;align-items:center;gap:.5rem;font-size:.875rem;color:var(--gray-700);cursor:pointer">
                     <input type="checkbox" class="ar-item" value="${c.id}" style="accent-color:var(--primary)" />
                     ${esc(c.label)}${c.sub ? ` <span style="color:var(--gray-400)">(${esc(c.sub)})</span>` : ""}
                   </label>`
                   )
                   .join("")}
               </div>`
        }
      </div>
      <div style="display:flex;gap:.5rem;justify-content:flex-end;padding:.875rem 1.25rem;border-top:1px solid var(--gray-100)">
        <button class="btn btn-secondary" id="ar-cancel">${empty ? "Schließen" : "Abbrechen"}</button>
        ${empty ? "" : `<button class="btn btn-primary" id="ar-save" disabled>Hinzufügen</button>`}
      </div>
    </div>`;
  document.body.appendChild(overlay);

  const close = () => {
    document.removeEventListener("keydown", onKey);
    overlay.remove();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      close();
    }
  };
  document.addEventListener("keydown", onKey);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector<HTMLButtonElement>("#ar-close")!.addEventListener("click", close);
  overlay.querySelector<HTMLButtonElement>("#ar-cancel")!.addEventListener("click", close);

  if (empty) return;

  const saveBtn = overlay.querySelector<HTMLButtonElement>("#ar-save")!;
  const allCb = overlay.querySelector<HTMLInputElement>("#ar-all")!;
  const itemCbs = [...overlay.querySelectorAll<HTMLInputElement>(".ar-item")];
  const getSelected = () => itemCbs.filter((cb) => cb.checked).map((cb) => Number(cb.value));
  const update = () => {
    saveBtn.disabled = getSelected().length === 0;
  };

  allCb.addEventListener("change", () => {
    itemCbs.forEach((cb) => (cb.checked = allCb.checked));
    update();
  });
  itemCbs.forEach((cb) =>
    cb.addEventListener("change", () => {
      allCb.checked = itemCbs.every((c) => c.checked);
      update();
    })
  );

  saveBtn.addEventListener("click", async () => {
    const ids = getSelected();
    if (ids.length === 0) return;
    saveBtn.disabled = true;
    try {
      const { added } = await api.sharedDocuments.addRecipients(doc.id, ids);
      showToast(added > 0 ? `${added} Empfänger ergänzt.` : "Keine neuen Empfänger ergänzt.");
      close();
      await refreshList(listEl);
    } catch (err: any) {
      alert("Ergänzen fehlgeschlagen: " + (err?.message || "unbekannter Fehler"));
      saveBtn.disabled = false;
    }
  });
}

function targetLabel(d: SharedDocument): string {
  if (d.targetKind === "practice") return "Praktikumsstellen";
  if (!d.targetClassIds || d.targetClassIds.length === 0) return "Alle aktiven Schüler";
  const names = d.targetClassIds.map((id) => classById.get(id)?.name || `#${id}`);
  return "Klassen: " + names.join(", ");
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function showToast(message: string) {
  const toast = document.createElement("div");
  toast.className = "toast";
  toast.textContent = message;
  document.body.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add("toast--visible"));
  setTimeout(() => {
    toast.classList.remove("toast--visible");
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}
